import type { OrderListFilters } from '@/api/order-hub/types';
import { ORDERS_HUB_FILTER_IDS } from './orderFilters';

/** Default sort matches the server's own default (`updated_at DESC`) so the
 *  no-param and explicit-param requests return identical pages. */
export const ORDERS_HUB_DEFAULT_SORT = 'updatedAt:DESC';
export const ORDER_LINES_DEFAULT_SORT = 'updatedAt:DESC';

export interface OrdersHubListParams {
  /** 1-based page (useTable convention). */
  page: number;
  count: number;
  sort?: string;
  search?: string;
  filters?: Record<string, string[]>;
  isArchived?: boolean;
}

type ListKey = {
  [K in keyof OrderListFilters]-?: OrderListFilters[K] extends
    | string[]
    | undefined
    ? K
    : never;
}[keyof OrderListFilters];

type ScalarKey = Exclude<keyof OrderListFilters, ListKey | 'isArchived'>;

const MULTI_VALUE_KEY_BY_FILTER_ID: Record<string, ListKey> = {
  [ORDERS_HUB_FILTER_IDS.orderType]: 'type',
  [ORDERS_HUB_FILTER_IDS.status]: 'status',
  [ORDERS_HUB_FILTER_IDS.tracingStatus]: 'tracingStatus',
  [ORDERS_HUB_FILTER_IDS.supplier]: 'supplier',
  [ORDERS_HUB_FILTER_IDS.buyer]: 'buyer',
  [ORDERS_HUB_FILTER_IDS.vendor]: 'vendor',
  [ORDERS_HUB_FILTER_IDS.receiver]: 'receiver',
  [ORDERS_HUB_FILTER_IDS.supplierCountry]: 'supplierCountry',
  [ORDERS_HUB_FILTER_IDS.internalResponsible]: 'internalResponsible',
  [ORDERS_HUB_FILTER_IDS.tags]: 'tag',
  [ORDERS_HUB_FILTER_IDS.seasons]: 'season',
  [ORDERS_HUB_FILTER_IDS.brands]: 'brand',
  [ORDERS_HUB_FILTER_IDS.styleCustomProperties]: 'styleCustomProperty',
  [ORDERS_HUB_FILTER_IDS.styleCollections]: 'styleCollection',
  [ORDERS_HUB_FILTER_IDS.styleType]: 'styleType',
};

const DATE_RANGE_KEYS_BY_FILTER_ID: Record<string, [ScalarKey, ScalarKey]> = {
  [ORDERS_HUB_FILTER_IDS.orderDate]: ['orderDateFrom', 'orderDateTo'],
  [ORDERS_HUB_FILTER_IDS.agreedDeliveryDate]: [
    'agreedDeliveryDateFrom',
    'agreedDeliveryDateTo',
  ],
  [ORDERS_HUB_FILTER_IDS.handoverDate]: ['handoverDateFrom', 'handoverDateTo'],
  [ORDERS_HUB_FILTER_IDS.actualDeliveryDate]: [
    'actualDeliveryDateFrom',
    'actualDeliveryDateTo',
  ],
};

const TIME_RANGE_KEYS_BY_FILTER_ID: Record<string, [ScalarKey, ScalarKey]> = {
  [ORDERS_HUB_FILTER_IDS.createdAt]: [
    'createdAfterUnixMs',
    'createdBeforeUnixMs',
  ],
  [ORDERS_HUB_FILTER_IDS.updatedAt]: [
    'updatedAfterUnixMs',
    'updatedBeforeUnixMs',
  ],
};

/** A picked day's bounds in the user's timezone. */
const startOfDayMs = (day: string) => new Date(`${day}T00:00:00`).getTime();
const endOfDayMs = (day: string) => new Date(`${day}T23:59:59.999`).getTime();

/**
 * The Order Number filter is one `;`-separated text value. The URL keeps filter
 * values comma-joined and splits them on read, so a number containing a comma
 * comes back as several values: re-join with `,` before splitting on `;`.
 */
export function parseOrderNumbers(vals: string[] | undefined): string[] {
  return (vals ?? [])
    .join(',')
    .split(';')
    .map(value => value.trim())
    .filter(Boolean);
}

/** Maps the table's filter state onto the API's filter params. */
export function buildOrderListFilters(
  filters: Record<string, string[]>,
): OrderListFilters {
  const out: OrderListFilters = {};

  for (const [id, key] of Object.entries(MULTI_VALUE_KEY_BY_FILTER_ID)) {
    const vals = filters[id]?.filter(Boolean);
    if (vals && vals.length > 0) out[key] = vals;
  }

  const orderNumbers = parseOrderNumbers(
    filters[ORDERS_HUB_FILTER_IDS.orderNumber],
  );
  if (orderNumbers.length > 0) out.orderNumbers = orderNumbers;

  for (const [id, [fromKey, toKey]] of Object.entries(
    DATE_RANGE_KEYS_BY_FILTER_ID,
  )) {
    const [from, to] = filters[id] ?? [];
    if (from) Object.assign(out, { [fromKey]: from });
    if (to) Object.assign(out, { [toKey]: to });
  }

  for (const [id, [afterKey, beforeKey]] of Object.entries(
    TIME_RANGE_KEYS_BY_FILTER_ID,
  )) {
    const [from, to] = filters[id] ?? [];
    if (from) Object.assign(out, { [afterKey]: startOfDayMs(from) });
    if (to) Object.assign(out, { [beforeKey]: endOfDayMs(to) });
  }

  return out;
}

function appendFilters(
  qs: URLSearchParams,
  filters: Record<string, string[]>,
): void {
  for (const [key, value] of Object.entries(buildOrderListFilters(filters))) {
    if (Array.isArray(value)) {
      qs.set(key, value.join(key === 'orderNumbers' ? ';' : ','));
    } else if (value !== undefined) {
      qs.set(key, String(value));
    }
  }
}

// The API rejects searches shorter than 2 chars (unindexable LIKE scan).
const MIN_SEARCH_LENGTH = 2;

export function cleanSearch(search: string | undefined): string | undefined {
  const trimmed = search?.trim();
  return trimmed && trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined;
}

function nonEmptyFilters(
  filters: Record<string, string[]> | undefined,
): Record<string, string[]> | undefined {
  const entries = Object.entries(filters ?? {}).filter(
    ([, vals]) => vals && vals.length > 0,
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

// Empty keys are dropped so the route loader and the list hook build identical query keys.
export function normalizeOrdersHubListParams(
  params: OrdersHubListParams,
): OrdersHubListParams {
  const search = cleanSearch(params.search);
  const filters = nonEmptyFilters(params.filters);
  return {
    page: params.page,
    count: params.count,
    ...(params.sort !== undefined ? { sort: params.sort } : {}),
    ...(search !== undefined ? { search } : {}),
    ...(filters !== undefined ? { filters } : {}),
    ...(params.isArchived ? { isArchived: true } : {}),
  };
}

/** Build the `GET /orders` query string. The API is 0-based on `page`. */
export function buildOrdersHubQs(params: OrdersHubListParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, params.page - 1)));
  qs.set('count', String(params.count));
  qs.set('sort', params.sort ?? ORDERS_HUB_DEFAULT_SORT);
  qs.set('isArchived', params.isArchived ? 'true' : 'false');

  const search = cleanSearch(params.search);
  if (search !== undefined) qs.set('search', search);

  appendFilters(qs, params.filters ?? {});
  return qs.toString();
}

/** Build the `GET /order-lines` query string for one order. `count` is capped
 *  at 100 by the API — the line count still comes from `meta.totalCount`, but
 *  sub-rows beyond 100 are truncated (documented limit). */
export function buildOrderLinesQs(orderId: string): string {
  const qs = new URLSearchParams();
  qs.set('orderId', orderId);
  qs.set('page', '0');
  qs.set('count', '100');
  return qs.toString();
}

export interface OrderLinesListParams {
  /** 1-based page (useTable convention). */
  page: number;
  count: number;
  sort?: string;
  search?: string;
  filters?: Record<string, string[]>;
}

// Empty keys are dropped so the route loader and the list hook build identical query keys.
export function normalizeOrderLinesListParams(
  params: OrderLinesListParams,
): OrderLinesListParams {
  const search = cleanSearch(params.search);
  const filters = nonEmptyFilters(params.filters);
  return {
    page: params.page,
    count: params.count,
    ...(params.sort !== undefined ? { sort: params.sort } : {}),
    ...(search !== undefined ? { search } : {}),
    ...(filters !== undefined ? { filters } : {}),
  };
}

/** Build the `GET /order-lines` query string for the standalone all-lines list
 *  (no `orderId` → every line the company owns). The API is 0-based on `page`
 *  and caps `count` at 100. */
export function buildAllOrderLinesQs(params: OrderLinesListParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, params.page - 1)));
  qs.set('count', String(Math.min(100, params.count)));
  qs.set('sort', params.sort ?? ORDER_LINES_DEFAULT_SORT);
  qs.set('isArchived', 'false');

  const search = cleanSearch(params.search);
  if (search !== undefined) qs.set('search', search);

  appendFilters(qs, params.filters ?? {});
  return qs.toString();
}
